import React, { FC } from 'react'
import { FieldRenderProps } from 'react-final-form'
import { DesktopTimePicker } from '@mui/x-date-pickers/DesktopTimePicker'
import { makeStyles } from 'tss-react/mui'
import { Theme } from '@mui/material/styles'
import { FormControl, FormHelperText, InputLabel } from '@mui/material'
import { Timer } from '@mui/icons-material'

const useRFFCustomTimePickerStyles = makeStyles()((theme: Theme) => ({
  pageInput: {
    display: 'flex',
  },

  formControl: {
    [theme.breakpoints.down('sm')]: {
      display: 'flex',
      marginBottom: theme.spacing(3),
    },
    [theme.breakpoints.up('sm')]: {
      display: 'flex',
      marginBottom: theme.spacing(3),
    },
  },

  timePickerFieldLabel: {
    position: 'relative',
  },

  datePicker: {
    display: 'flex',
    '& > div': {
      color: theme.palette.primary.dark,
    },
    marginTop: theme.spacing(2),
    marginBottom: 0,
  },

  inputLabel: {
    position: 'relative',
    marginBottom: theme.spacing(2),
    color: theme.palette.info.dark,
    fontSize: theme.spacing(2),
    lineHeight: 1.4, // 22
  },
}))

type ParsableTime = Date | number | string | null | undefined

// v4 pickers parsed any of these; the v7 date-fns adapter accepts only Date.
const toDate = (value: ParsableTime): Date | null =>
  value === null || value === undefined || value === '' ? null : new Date(value)

interface RFFCustomTimePickerProps extends FieldRenderProps<ParsableTime> {
  fieldLabel?: string
  fieldPlaceholder?: string
  fieldInputVariant?: 'standard' | 'outlined' | 'filled'
  customClass?: string
}

const RFFCustomTimePicker: FC<RFFCustomTimePickerProps> = ({ input, meta, ...props }) => {
  const { classes } = useRFFCustomTimePickerStyles()

  const isError: boolean = (meta.error || meta.submitError) && !meta.pristine

  return (
    <FormControl variant='standard' error={isError} className={classes.formControl}>
      {!!props.fieldLabel && (
        <InputLabel id={props.fieldLabel} className={classes.inputLabel} shrink>
          {props.fieldLabel}
        </InputLabel>
      )}
      {/* Desktop variant everywhere, matching v4's inline KeyboardTimePicker. */}
      <DesktopTimePicker<Date>
        format='HH:mm'
        ampm={false}
        value={toDate(input.value)}
        onChange={input.onChange}
        slots={{ openPickerIcon: Timer }}
        slotProps={{
          textField: {
            variant: props.fieldInputVariant || 'standard',
            className: props.customClass,
            placeholder: props.fieldPlaceholder,
            InputLabelProps: {
              shrink: true,
              variant: 'standard',
              classes: {
                root: classes.timePickerFieldLabel,
              },
            },
            helperText: '', // Need to disable component validation
          },
          openPickerButton: { edge: false }, // as in RFFCustomDatePicker
        }}
      />
      <FormHelperText>{meta.error}</FormHelperText>
    </FormControl>
  )
}

export default RFFCustomTimePicker
